import { ReactElement, useContext, useEffect } from 'react';
import { LoadType, Loads, PointLoad, DistributedLoad } from 'types/domainTypes';
import { Grid, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LabelledText } from 'components/LabelledText/LabelledText';
import { DeleteOutline, Edit } from '@mui/icons-material';
import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/system/styleFunctionSx';
import { CalculationContext } from './CalculationContextProvider';

interface IconProps {
  onClick: () => void;
}

export function EditIcon({ onClick }: IconProps): ReactElement {
  return (
    <IconButton size="small" style={{ marginTop: '-8px', marginLeft: '15px' }} onClick={onClick} color="primary">
      <Edit
        style={{
          width: '20px',
        }}
      />
    </IconButton>
  );
}
export function DeleteIcon({ onClick }: IconProps): ReactElement {
  return (
    <IconButton size="small" style={{ marginTop: '-8px', width: '20px' }} onClick={onClick} color="primary">
      <DeleteOutline
        style={{
          width: '20px',
        }}
      />
    </IconButton>
  );
}

interface LoadsPanelProps {
  tableHeading?: string | undefined;
  loadLabel?: string;
  loadType: LoadType;
  loads?: Loads;
  onEditUniform?: () => void | undefined;
  onDeleteUniform?: () => void | undefined;
  onEditPointLoad?: (pointLoad: PointLoad) => void | undefined;
  onDeletePointLoad?: (pointLoad: PointLoad) => void | undefined;
  onEditDistributedLoad?: (pointLoad: DistributedLoad) => void | undefined;
  onDeleteDistributedLoad?: (pointLoad: DistributedLoad) => void | undefined;
  sx?: SxProps<Theme>;
}

export function LoadsPanel({
  tableHeading,
  loadLabel,
  loads,
  onEditUniform,
  onDeleteUniform,
  onEditPointLoad,
  onDeletePointLoad,
  onEditDistributedLoad,
  onDeleteDistributedLoad,
  sx,
}: LoadsPanelProps): ReactElement {
  const { t } = useTranslation();
  const { product } = useContext(CalculationContext);
  const showSelfWeight = loadLabel && product && loadLabel === 'Permanent load';
  const selfWeightAsText = `${product?.selfWeight} kN/m²`;

  return (
    <Grid item xs={12}>
      <TableContainer component={Paper} sx={sx}>
        <Table>
          {tableHeading && (loads?.uniformLoad || loads?.pointLoads || loads?.distributedLoads) && (
            <TableHead>
              <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }} data-testid="tablerow">
                <TableCell colSpan={3} sx={{ padding: '0 16px' }}>
                  <Typography variant="h6" fontFamily="KingspanBrownTT-Bold">
                    {tableHeading}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
          )}
          <TableBody>
            {showSelfWeight && (
              <TableRow key={'selfWeight'} data-testid="selfweight-row">
                <TableCell style={{ verticalAlign: 'top', width: '150px', paddingRight: 0 }}>
                  <LabelledText
                    label="calculation-page.label-type-of-load"
                    value={t('calculation-page.spans-and-loads-dialog.label-self-weight-load')}
                  />
                </TableCell>
                <TableCell colSpan={2}>
                  {loadLabel && <LabelledText label={loadLabel} value={selfWeightAsText} data-testid="selfweight-value" />}
                </TableCell>
                <TableCell
                  style={{
                    textAlign: 'right',
                    verticalAlign: 'top',
                    borderBottom: '1px solid rgba(224, 224, 224, 1)',
                  }}
                ></TableCell>
              </TableRow>
            )}
            {loads?.uniformLoad && (
              <TableRow key={'uniform'} sx={{ '&:last-child td, &:last-child th': { border: 0 } }} data-testid="tablerow">
                <TableCell style={{ verticalAlign: 'top', width: '150px' }}>
                  <LabelledText label="calculation-page.label-type-of-load" value={t('calculation-page.uniform-load')} />
                </TableCell>
                <TableCell colSpan={2}>{loadLabel && <LabelledText label={loadLabel} value={`${loads.uniformLoad} kN/m²`} />}</TableCell>
                <TableCell
                  style={{
                    textAlign: 'right',
                    verticalAlign: 'top',
                  }}
                >
                  {onEditUniform && <EditIcon onClick={onEditUniform} />}
                  {onDeleteUniform && <DeleteIcon onClick={onDeleteUniform} />}
                </TableCell>
              </TableRow>
            )}
            {loads?.pointLoads &&
              loads?.pointLoads.map((pointLoad, index) => (
                <TableRow key={`pointLoad${index}`} sx={{ '&:last-child td, &:last-child th': { border: 0 } }} data-testid="tablerow">
                  <TableCell style={{ verticalAlign: 'top' }}>
                    <LabelledText label="calculation-page.label-type-of-load" value={t('calculation-page.point-load')} />
                  </TableCell>
                  <TableCell>
                    <LabelledText label="calculation-page.label-location" value={`${pointLoad.position} m`} />
                  </TableCell>
                  <TableCell>
                    <LabelledText label="calculation-page.label-load" value={`${pointLoad.load} kN`} />
                  </TableCell>
                  <TableCell style={{ textAlign: 'right', verticalAlign: 'top' }}>
                    {onEditPointLoad && (
                      <EditIcon
                        onClick={() => {
                          onEditPointLoad(pointLoad);
                        }}
                      />
                    )}
                    {onDeletePointLoad && (
                      <DeleteIcon
                        onClick={() => {
                          onDeletePointLoad(pointLoad);
                        }}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            {loads?.distributedLoads &&
              loads?.distributedLoads.map((distributedLoad, index) => (
                <TableRow key={`pointLoad${index}`} sx={{ '&:last-child td, &:last-child th': { border: 0 } }} data-testid="tablerow">
                  <TableCell style={{ verticalAlign: 'top' }}>
                    <LabelledText label="calculation-page.label-type-of-load" value={t('calculation-page.distributed-load')} />
                  </TableCell>
                  <TableCell>
                    <LabelledText label="calculation-page.label-start-location" value={`${distributedLoad.positionStart} m`} />
                    <br />
                    <LabelledText label="calculation-page.label-end-location" value={`${distributedLoad.positionEnd} m`} />
                  </TableCell>
                  <TableCell>
                    <LabelledText label="calculation-page.label-start-load" value={`${distributedLoad.loadStart} kN/m²`} />
                    <br />
                    <LabelledText label="calculation-page.label-end-load" value={`${distributedLoad.loadEnd} kN/m²`} />
                  </TableCell>
                  <TableCell style={{ textAlign: 'right', verticalAlign: 'top' }}>
                    {onEditDistributedLoad && (
                      <EditIcon
                        onClick={() => {
                          onEditDistributedLoad(distributedLoad);
                        }}
                      />
                    )}
                    {onDeleteDistributedLoad && (
                      <DeleteIcon
                        onClick={() => {
                          onDeleteDistributedLoad(distributedLoad);
                        }}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Grid>
  );
}
